import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Button, Modal, Tooltip } from "antd";
import { X } from "@phosphor-icons/react";
import { getDirection, t, useStoreLanguage } from "../../features/store-language/i18n.ts";
import { PendingRing } from "./LoadingRing.jsx";
import "../../features/platform-admin/platform-admin-tokens.css";
import "./PortalDialog.css";

type PortalDialogProps = {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number | string;
  busy?: boolean;
  destructive?: boolean;
  maskClosable?: boolean;
  className?: string;
  afterOpenChange?: (open: boolean) => void;
};

/** React 18 / Ant 5 adapter of Raj's owned Dialog; chrome stays here. */
export default function PortalDialog({ open, title, children, footer, onClose, width = 512,
  busy = false, destructive = false, maskClosable = false, className = "", afterOpenChange }: PortalDialogProps) {
  useStoreLanguage();
  const actions = useRef<HTMLDivElement>(null);
  useLayoutEffect(function () {
    if (!open) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Imperative confirmations unmount immediately; Ant's exit animation cannot restore focus then.
    return function () { if (trigger?.isConnected) trigger.focus({ preventScroll: true }); };
  }, [open]);
  function close() {
    if (!busy) onClose();
  }
  return (
    <Modal
      open={open}
      centered
      closable={false}
      destroyOnClose
      width={width}
      zIndex={3000}
      rootClassName={`portal-dialog ${className}`}
      getContainer={function () {
        return document.querySelector<HTMLElement>(".platform-admin")
          || document.querySelector<HTMLElement>(".portal-shell")
          || document.querySelector<HTMLElement>(".shop-layout")
          || document.body;
      }}
      onCancel={close}
      maskClosable={maskClosable && !busy}
      keyboard={!busy}
      afterOpenChange={function (visible) {
        if (visible && destructive) actions.current?.querySelector<HTMLButtonElement>("[data-dialog-cancel]")?.focus();
        afterOpenChange?.(visible);
      }}
      title={(
        <span className="portal-dialog__heading" dir={getDirection()}>
          <span className="portal-dialog__title">{title}</span>
          <Tooltip title={t("بستن پنجره")}>
            <Button
              className="portal-dialog__close"
              type="text"
              disabled={busy}
              onClick={close}
              icon={<X size={20} aria-hidden="true" />}
            >
              <span className="portal-dialog__sr-only">{t("بستن پنجره")}</span>
            </Button>
          </Tooltip>
        </span>
      )}
      footer={footer == null ? null : (
        <div className="portal-dialog__actions" dir={getDirection()} ref={actions}>
          {footer}
        </div>
      )}
    >
      <div className="portal-dialog__body" dir={getDirection()} aria-busy={busy}>
        {children}
      </div>
    </Modal>
  );
}

export function PortalDialogAction({ children, onClick, primary = false, danger = false, pending = false,
  disabled = false, cancel = false }: {
  children: ReactNode; onClick: () => void; primary?: boolean; danger?: boolean;
  pending?: boolean; disabled?: boolean; cancel?: boolean;
}) {
  return (
    <Button
      type={primary ? "primary" : "default"}
      danger={danger}
      disabled={disabled || pending}
      aria-busy={pending}
      data-dialog-cancel={cancel || undefined}
      icon={pending ? <PendingRing /> : undefined}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
